import React from "react";
import { Link } from "react-router-dom";
import { launchGoogleAuth } from "../lib/authLib";
import { useSnackbar } from "../contexts/SnackbarContext";

const Signup = () => {
  const { showSnackbar } = useSnackbar();

  const handleSignup = async () => {
    try {
      const result = await launchGoogleAuth();
      window.open(result.url, "_self");
    } catch (error) {
      console.error("Error during Google authentication", error);

      if (error instanceof Error) {
        showSnackbar(error.message, "error");
      } else {
        showSnackbar("Failed to connect to the Cobutler API", "error");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 px-4 py-10">
      <div className="mx-auto w-full max-w-5xl">
        <div className="overflow-hidden rounded-lg border border-gray-700 bg-gray-800 shadow-xl">
          <div className="grid lg:grid-cols-2">
            <div className="border-b border-gray-700 p-6 sm:p-8 lg:border-b-0 lg:border-r">
              <img
                src="/logo-dark.png"
                alt="Cobutler Logo"
                className="mb-6 h-auto w-48"
              />

              <div className="max-w-xl">
                <h1 className="mb-4 text-3xl font-bold leading-tight text-white sm:text-4xl">
                  Silence the noise.
                </h1>

                <p className="mb-6 text-lg text-gray-300">
                  Allow the senders you care about, filter the rest.
                </p>

                <div className="mb-8 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-md bg-gray-700 p-2 text-blue-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 7h18M6 12h12M9 17h6"
                        />
                      </svg>
                    </div>
                    <div>
                      <h2 className="font-semibold text-white">
                        Prioritize real relationships
                      </h2>
                      <p className="text-sm text-gray-300">
                        We look at who you actually email so the people you know
                        and trust can reach you without getting buried.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-md bg-gray-700 p-2 text-blue-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M18.364 5.636l-1.414 1.414M7.05 16.95l-1.414 1.414M5.636 5.636l1.414 1.414m9.9 9.9l1.414 1.414M12 8v4l3 3"
                        />
                      </svg>
                    </div>
                    <div>
                      <h2 className="font-semibold text-white">
                        Cut junk before it steals your attention
                      </h2>
                      <p className="text-sm text-gray-300">
                        Promotional noise, cold outreach, and random inbound
                        mail gets filtered out before it ever clutters your
                        inbox.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-md bg-gray-700 p-2 text-blue-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 1.12-3 2.5S10.343 13 12 13s3 1.12 3 2.5S13.657 18 12 18m0-10V6m0 12v-2m9-4a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h2 className="font-semibold text-white">
                        Turn junk into something useful
                      </h2>
                      <p className="text-sm text-gray-300">
                        Enable Challenges and Cobutler can automatically reply
                        to unwanted senders, asking them to donate to your
                        chosen charity. If they donate, their email can be let
                        back through.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-300">
                        <span className="font-semibold text-white">
                          Pricing: $5/mo, additional inboxes for $1/mo
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8">
              <div className="mx-auto max-w-md">
                <h2 className="mb-2 text-2xl font-bold text-white text-center">
                  Get started with Google
                </h2>
                <p className="mb-6 text-gray-300 text-center">
                  Create your account in under a minute and connect the inbox
                  you want Cobutler to protect.
                </p>

                <div className="mb-6 flex gap-3 rounded-lg border border-amber-500/35 bg-amber-950/45 p-3 sm:p-4">
                  <div className="shrink-0 pt-0.5 text-amber-400">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm leading-relaxed text-amber-100/95">
                    During the beta period we need to pre-authorize your email
                    address in our Google API account. Just reach out to{" "}
                    <a
                      href="mailto:hello@cobutler.com"
                      className="font-medium text-amber-200 underline decoration-amber-400/70 underline-offset-2 hover:text-amber-100"
                    >
                      hello@cobutler.com
                    </a>{" "}
                    if you need help!
                  </p>
                </div>

                <div className="mb-6 rounded-lg border border-gray-700 bg-gray-900/50 p-4">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-400 text-center">
                    How it works
                  </h3>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
                        1
                      </div>
                      <div>
                        <p className="font-medium text-white text-center sm:text-left">
                          Log in with Google
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
                        2
                      </div>
                      <div>
                        <p className="font-medium text-white text-center sm:text-left">
                          Review your inbox rules
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
                        3
                      </div>
                      <div>
                        <p className="font-medium text-white text-center sm:text-left">
                          Enable Challenges (if you want)
                        </p>
                        <p className="text-sm text-gray-300 text-center sm:text-left">
                          Challenge unwanted senders to donate to your chosen
                          charity before their message is allowed through.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
                        4
                      </div>
                      <div>
                        <p className="font-medium text-white text-center sm:text-left">
                          Enjoy your inbox again
                        </p>
                      </div>
                    </div>{" "}
                  </div>
                </div>

                <button
                  onClick={handleSignup}
                  className="w-full rounded-md bg-blue-600 px-4 py-3 font-medium text-white transition-colors duration-200 hover:bg-blue-700"
                >
                  <span className="flex items-center justify-center gap-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z" />
                    </svg>
                    Continue with Google
                  </span>
                </button>

                <p className="mt-4 text-center text-xs leading-5 text-gray-400">
                  By creating an account you're agreeing to our{" "}
                  <Link
                    to="/terms"
                    className="text-blue-400 hover:text-blue-300"
                  >
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy"
                    className="text-blue-400 hover:text-blue-300"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>

                <div className="mt-8 text-center">
                  <a
                    href="mailto:hello@cobutler.com"
                    className="inline-flex items-center justify-center gap-2 text-sm text-blue-400 hover:text-blue-300"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Questions? Email hello@cobutler.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 bg-gray-700 px-6 py-4 text-center text-sm text-gray-300">
            <p>© {new Date().getFullYear()} Cobutler. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
